import { useState } from 'react';
import type { CSSProperties } from 'react';
import { useLanguage } from './language-context';
import { nameInitial } from './name-initial';
import type { User } from '../mock/seeds/schema-types';
import './avatar.css';

// The customer's avatar, everywhere in the dashboard (top bar, account menu, Account
// profile card). One round box of the asked size whatever it shows: the uploaded picture
// (`avatar_url`), or — no picture, or the picture failed to load — an accent-tinted
// circle with the name's initial (nameInitial: grapheme-aware for Bangla), or a neutral
// person glyph when there is no name either (e.g. while /me is still loading). A failed
// URL is remembered so the fallback sticks until the URL changes (a new upload).
// Decorative: the name is always beside it or in the control's accessible name.
const PERSON = 'M12 12a3.8 3.8 0 1 0 0-7.6 3.8 3.8 0 0 0 0 7.6M4.5 20c1.3-3.6 4.2-5.4 7.5-5.4s6.2 1.8 7.5 5.4';

export function Avatar({
  user,
  size,
  className,
}: {
  user: Pick<User, 'name' | 'avatar_url'> | undefined;
  size: number;
  className?: string;
}) {
  const { lang } = useLanguage();
  const src = user?.avatar_url || '';
  const [failed, setFailed] = useState<string | null>(null);
  const cls = className ? `avatar-dot ${className}` : 'avatar-dot';
  const box: CSSProperties = { width: size, height: size };

  if (src && failed !== src) {
    return (
      <img
        className={`${cls} avatar-dot--img`}
        src={src}
        alt=""
        width={size}
        height={size}
        style={box}
        decoding="async"
        onError={() => setFailed(src)}
      />
    );
  }

  const initial = nameInitial(user?.name, lang);
  // ~40% of the box for 32–40 px chips, ~31% for the large profile face (design sizes).
  const fontSize = Math.round(size * (size <= 44 ? 0.41 : 0.31));
  return (
    <span className={`${cls} avatar-dot--fallback`} style={{ ...box, fontSize }} aria-hidden="true" data-fallback={initial ? 'initial' : 'icon'}>
      {initial || (
        <svg viewBox="0 0 24 24" width={Math.round(size * 0.56)} height={Math.round(size * 0.56)} fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <path d={PERSON} />
        </svg>
      )}
    </span>
  );
}
